import { ExportProgressReporter } from "../components/CustomButton";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Card,
  CardContent,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import PaymentsIcon from "@mui/icons-material/Payments";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import {
  ADMIN_DATA_SUBTITLES,
  adminNavItemSx,
  adminNavPanelSx,
  adminPageSx,
  adminNavTitleSx,
} from "../components/adminData/adminNavStyles";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import { COLORS } from "../assets/theme";
import { IPageable, Params } from "../types/table/interfaces.type";
import { getAllBusinesses } from "../services/VenueService";
import { getCashflowReport } from "../services/newReportsService";
import PinAccessDialog from "../components/PinAccessDialog";
import {
  getCachedPinAccess,
  subscribeToPinAccess,
} from "../utils/pinAccessCache";
import { formatTableDate } from "../utils/tableDateFormat";

type AdminTab = "clients" | "finance" | "expenses" | "reports" | "notifications";

type ReportType = "expense" | "profit";

interface AdminReportRow {
  id: string;
  title: string;
  amount: number;
  totalAmount?: number;
  paidAmount?: number;
  remainingAmount?: number;
  date: string;
  business_id?: string;
  business_name?: string;
  description?: string;
}

type PaymentStatus = "paid" | "partial" | "unpaid" | "n/a";

const pickNonEmptyString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return undefined;
};

const parseDateValue = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (!Number.isNaN(parsed.getTime())) return parsed;

  const match = value.match(
    /^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s*(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/,
  );
  if (!match) return null;
  const [, day, month, year, hour, minute, second] = match;
  const d = Number(day);
  const m = Number(month) - 1;
  const y = Number(year);
  const h = Number(hour || "0");
  const min = Number(minute || "0");
  const sec = Number(second || "0");
  const date = new Date(y, m, d, h, min, sec);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseAmountValue = (value?: unknown): number => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const cleaned = Number(value.replace(/[^0-9.-]+/g, ""));
    if (Number.isFinite(cleaned)) return cleaned;
  }
  return 0;
};

const resolveDateString = (item: any): string => {
  const candidates = [
    item?.appointmentStartAt,
    item?.appointment?.start_at,
    item?.appointment?.startAt,
    item?.start_at,
    item?.startAt,
    item?.invoice_date,
    item?.invoice?.invoice_date,
    item?.invoice?.created_at,
    item?.created_at,
    item?.createdAt,
    item?.date,
    item?.expense_date,
  ];

  for (const c of candidates) {
    if (!c) continue;
    const d = new Date(c);
    if (!Number.isNaN(d.getTime())) {
      return d.toISOString();
    }
  }
  return "";
};

const getPaymentStatus = (row: AdminReportRow): PaymentStatus => {
  const total = parseAmountValue(row.totalAmount);
  const paid = parseAmountValue(row.paidAmount);
  const remaining =
    row.remainingAmount === undefined || row.remainingAmount === null
      ? total > 0
        ? Math.max(total - paid, 0)
        : 0
      : parseAmountValue(row.remainingAmount);

  const epsilon = 0.0001;
  if (remaining > epsilon) {
    return paid <= epsilon ? "unpaid" : "partial";
  }

  if (total > epsilon || paid > epsilon) return "paid";
  return "n/a";
};

const normalizeReportType = (value?: unknown): ReportType => {
  const normalized = String(value || "")
    .trim()
    .toLowerCase();
  return normalized === "expense" ? "expense" : "profit";
};

const normalizeReportRow = (
  item: any,
  type: ReportType,
  index: number,
): AdminReportRow => {
  const businessIdCandidate =
    item?.business_id ??
    item?.businessId ??
    item?.business?.id ??
    item?.invoice?.business_id ??
    item?.invoice?.businessId ??
    item?.invoice?.business?.id;
  const businessName =
    pickNonEmptyString(
      item?.business_name,
      item?.businessName,
      item?.business?.name,
      item?.invoice?.business_name,
      item?.invoice?.business?.name,
    ) || (businessIdCandidate ? `Business ${businessIdCandidate}` : "");
  const amountCandidate =
    item?.amount ??
    item?.net_amount ??
    item?.total_amount ??
    item?.invoice?.net_amount ??
    item?.invoice?.total_amount ??
    item?.paid_amount ??
    item?.price;
  const dateValue = resolveDateString(item);
  const titleValue =
    pickNonEmptyString(
      item?.title,
      item?.name,
      item?.invoice_title,
      item?.invoice_number,
      item?.Invoice_number,
      item?.invoice?.invoice_number,
      item?.invoice?.Invoice_number,
      item?.client_name,
      item?.client?.name,
      item?.client?.full_name,
      item?.client?.fullName,
      item?.description,
    ) || "";
  const descriptionValue =
    item?.description ||
    item?.notes ||
    item?.note ||
    item?.invoice?.notes ||
    "";
  const resolvedDescription =
    type === "profit" && !String(descriptionValue).trim()
      ? "Paid invoice"
      : descriptionValue;
  const idCandidate =
    item?.id ||
    item?._id ||
    item?.expense_id ||
    item?.expenseId ||
    item?.invoice_id ||
    item?.invoice?.id ||
    item?.invoice?.invoice_id;

  return {
    id: String(idCandidate || `${type}-${index}`),
    title: titleValue,
    description: resolvedDescription,
    amount: parseAmountValue(amountCandidate),
    date: dateValue,
    business_name: businessName,
    business_id:
      businessIdCandidate !== undefined && businessIdCandidate !== null
        ? String(businessIdCandidate)
        : undefined,
  };
};

const sortByDate = (rows: AdminReportRow[]): AdminReportRow[] => {
  return [...rows].sort((a, b) => {
    const aDate = parseDateValue(a.date || "");
    const bDate = parseDateValue(b.date || "");
    if (!aDate && !bDate) return 0;
    if (!aDate) return 1;
    if (!bDate) return -1;
    return bDate.getTime() - aDate.getTime();
  });
};

const normalizeReportResponse = (response: any): AdminReportRow[] => {
  const expenses =
    response?.expenses || response?.data?.expenses || response?.data?.expense;
  const profits =
    response?.profits || response?.data?.profits || response?.data?.profit;

  if (Array.isArray(expenses) || Array.isArray(profits)) {
    const normalizedExpenses = (Array.isArray(expenses) ? expenses : []).map(
      (item: any, index: number) => normalizeReportRow(item, "expense", index),
    );
    const normalizedProfits = (Array.isArray(profits) ? profits : []).map(
      (item: any, index: number) => normalizeReportRow(item, "profit", index),
    );
    return sortByDate([...normalizedProfits, ...normalizedExpenses]);
  }

  const list = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
      ? response
      : [];

  if (!Array.isArray(list)) return [];

  return sortByDate(
    list.map((item: any, index: number) =>
      normalizeReportRow(item, normalizeReportType(item?.type), index),
    ),
  );
};

const ADMIN_REPORTS_PAGE_SIZE = 30;

const AdminReports: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "reports";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [reports, setReports] = useState<AdminReportRow[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [financeUnlocked, setFinanceUnlocked] = useState(() =>
    getCachedPinAccess()
  );
  const [pinDialogOpen, setPinDialogOpen] = useState(() =>
    !getCachedPinAccess()
  );
  const [reportDateRange, setReportDateRange] = useState<
    { start?: string; end?: string } | [string?, string?] | null
  >(() => {
    const now = new Date();
    const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0));
    const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 23, 59, 59, 999));
    return { start: start.toISOString(), end: end.toISOString() };
  });

  const buildDateBoundary = (raw: string | Date | null | undefined, endOfDay: boolean) => {
    if (!raw) return null;
    if (typeof raw === "string" && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
      const [y, m, d] = raw.split("-").map(Number);
      return new Date(Date.UTC(y, m - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0));
    }
    const parsed = raw instanceof Date ? raw : new Date(raw);
    if (Number.isNaN(parsed.getTime())) return null;
    return new Date(
      Date.UTC(
        parsed.getUTCFullYear(),
        parsed.getUTCMonth(),
        parsed.getUTCDate(),
        endOfDay ? 23 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 59 : 0,
        endOfDay ? 999 : 0,
      ),
    );
  };

  const normalizeRangeIso = (range: { start?: string; end?: string } | [string?, string?] | null) => {
    if (!range) return { from: undefined as string | undefined, to: undefined as string | undefined };
    const startRaw = Array.isArray(range) ? range[0] : range.start;
    const endRaw = Array.isArray(range) ? range[1] : range.end;
    const fromDate = buildDateBoundary(startRaw as any, false);
    const toDate = buildDateBoundary(endRaw as any, true);
    return {
      from: fromDate ? fromDate.toISOString() : undefined,
      to: toDate ? toDate.toISOString() : undefined,
    };
  };
  const [businesses, setBusinesses] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [businessFilter, setBusinessFilter] = useState<string>("all");
  const navigateTo = useCallback(
    (path: string) => {
      if (!financeUnlocked) {
        setPinDialogOpen(true);
        return;
      }
      navigate(path);
    },
    [financeUnlocked, navigate],
  );

  const currencyFormatter = useMemo(
    () =>
      new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }),
    [],
  );

  const formatMoney = useCallback(
    (value?: number | string | null) => {
      if (value === null || value === undefined || value === "") return "-";
      const num = Number(value);
      if (!Number.isFinite(num)) return String(value);
      return currencyFormatter.format(num);
    },
    [currencyFormatter]
  );

  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await getAllBusinesses();
        const mapped = (Array.isArray(response) ? response : [])
          .map((business: any) => {
            // Expect stable { id, name } for businesses.
            const id = business?.id;
            if (!id) return null;
            const name = pickNonEmptyString(business?.name) || `Business ${id}`;
            return { id: String(id), name: String(name) };
          })
          .filter(Boolean) as { id: string; name: string }[];
        setBusinesses(mapped);
      } catch (error) {
        console.error("Error fetching businesses:", error);
        setBusinesses([]);
      }
    };
    fetchBusinesses();
  }, []);
  useEffect(() => {
    const unsubscribe = subscribeToPinAccess((value) => {
      setFinanceUnlocked(value);
      if (!value) setPinDialogOpen(true);
    });
    return unsubscribe;
  }, []);
  useEffect(() => {
    if (financeUnlocked) setPinDialogOpen(false);
  }, [financeUnlocked]);

  const handlePinVerified = () => {
    setFinanceUnlocked(true);
    setPinDialogOpen(false);
  };

  type CashflowResponse = {
    income?: {
      rows?: any[];
    };
    expenses?: {
      rows?: any[];
    };
    summary?: any;
  };
  // One page of income and one of expenses from the server, which filters
  // and searches them; its summary covers the whole period.
  const loadReportsPage = useCallback(
    async ({
      page,
      size,
      search: searchTerm,
    }: {
      page: number;
      size: number;
      search?: string;
    }) => {
      const { from, to } = normalizeRangeIso(reportDateRange);

      const selectedBusinessId =
        businessFilter !== "all" ? String(businessFilter) : undefined;

      const res = (await getCashflowReport({
        from: from!,
        to: to!,
        businessId: selectedBusinessId,
        page,
        size,
        search: searchTerm,
      })) as any;

      const incomeRows =
        res?.income?.rows?.map((r: any) => ({
          id: `income-${r.invoiceId}`,
          title: `Invoice #${r.invoiceId} - ${r.clientName || "Client"}`,
          amount: Number(r.moneyReceived || 0),
          totalAmount: r.totalAmount,
          paidAmount: r.moneyReceived,
          remainingAmount: r.remainingAmount,
          date: r.invoiceDate,
          business_id: r.business?.id,
          business_name: r.business?.name,
          description: r.serviceName,
        })) || [];

      const expenseRows =
        res?.expenses?.rows?.map((e: any, i: number) => ({
          id: `expense-${e.expenseId || `${page}-${i}`}`,
          title: e.expenseName || "Expense",
          amount: -Math.abs(Number(e.amount || 0)),
          totalAmount: Math.abs(Number(e.amount || 0)),
          paidAmount: Math.abs(Number(e.amount || 0)),
          remainingAmount: 0,
          date: e.expenseDate,
          business_id: e.business?.id,
          business_name: e.business?.name,
          description: e.expenseName,
        })) || [];

      const rows: AdminReportRow[] = [...incomeRows, ...expenseRows].sort(
        (a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime(),
      );
      const incomeTotal = Number(res?.income?.pagination?.totalElements) || 0;
      const expenseTotal = Number(res?.expenses?.pagination?.totalElements) || 0;
      return {
        rows,
        totalElements: incomeTotal + expenseTotal,
        totalPages: Math.max(1, Number(res?.totalPages) || 1),
        summary: res?.summary ?? null,
      };
    },
    [reportDateRange, businessFilter],
  );

  const [reportsSummary, setReportsSummary] = useState<any | null>(null);
  const debouncedSearch = useDebouncedValue(search, 400);

  // Period totals summed by the server over every invoice and expense; a
  // search narrows the rows below but not these cards.
  const summaryTotals = useMemo(
    () => ({
      // These two cards have always counted paid expenses as well; kept so the
      // figures users know do not move.
      invoiced:
        (Number(reportsSummary?.totalInvoiced) || 0) +
        (Number(reportsSummary?.totalExpensesPaid) || 0),
      received:
        (Number(reportsSummary?.totalReceived) || 0) +
        (Number(reportsSummary?.totalExpensesPaid) || 0),
      outstanding: Number(reportsSummary?.totalOutstanding) || 0,
      expenses: Number(reportsSummary?.totalExpensesPaid) || 0,
      net: Number(reportsSummary?.netCash) || 0,
    }),
    [reportsSummary],
  );
  const netTotal = summaryTotals.net;

  const fetchReportsTable = useCallback(
    async (params?: any): Promise<IPageable<AdminReportRow>> => {
      const page = Number(params?.page) || 0;
      const size = Number(params?.size) || ADMIN_REPORTS_PAGE_SIZE;
      if (!financeUnlocked) {
        return { content: [], number: page, size, totalElements: 0, totalPages: 1 };
      }
      setLoadingReports(true);
      try {
        const result = await loadReportsPage({
          page,
          size,
          search: String(params?.search || ""),
        });
        setReports(result.rows);
        setReportsSummary(result.summary);
        setTotalElements(result.totalElements);
        return {
          content: result.rows,
          number: page,
          size,
          totalElements: result.totalElements,
          totalPages: result.totalPages,
        };
      } catch (error) {
        console.error("Error fetching cashflow reports:", error);
        setReports([]);
        setReportsSummary(null);
        throw error;
      } finally {
        setLoadingReports(false);
      }
    },
    [financeUnlocked, loadReportsPage],
  );

  const exportReportsCsv = useCallback(async (reportProgress?: ExportProgressReporter) => {
    // The table holds one page; the file gets the whole period.
    const filteredReports: AdminReportRow[] = [];
    try {
      for (let page = 0; page < 1000; page += 1) {
        const result = await loadReportsPage({ page, size: 200, search: debouncedSearch });
        filteredReports.push(...result.rows);
        reportProgress?.(page + 1, result.totalPages);
        if (page + 1 >= result.totalPages || result.rows.length === 0) break;
      }
    } catch (error) {
      console.error("Error exporting cashflow reports:", error);
      return;
    }
    if (filteredReports.length === 0) return;
    const keys = [
      "title",
      "totalAmount",
      "paidAmount",
      "remainingAmount",
      "amount",
      "date",
      "business_name",
    ];
    const header = keys.join(",");
    const lines = filteredReports.map((r: any) => {
      const row: Record<string, any> = {
        title: r.title ?? "-",
        totalAmount:
          r.totalAmount === undefined || r.totalAmount === null
            ? "-"
            : currencyFormatter.format(parseAmountValue(r.totalAmount)),
        paidAmount:
          r.paidAmount === undefined || r.paidAmount === null
            ? "-"
            : currencyFormatter.format(parseAmountValue(r.paidAmount)),
        remainingAmount:
          r.remainingAmount === undefined || r.remainingAmount === null
            ? "-"
            : currencyFormatter.format(parseAmountValue(r.remainingAmount)),
        amount: (() => {
          const val = Number(r.amount || 0);
          const formatted = currencyFormatter.format(Math.abs(val));
          if (val > 0) return `+${formatted}`;
          if (val < 0) return `-${formatted}`;
          return formatted;
        })(),
        date: (() => {
          const d = new Date(r.date);
          return Number.isNaN(d.getTime()) ? "-" : formatTableDate(d);
        })(),
        business_name: r.business_name ?? "-",
      };

      return keys
        .map((k) => `"${String(row[k] ?? "-").replace(/"/g, '""')}"`)
        .join(",");
    });

    const sections: string[] = [header, ...lines];

    // Append financial summary (same as cards)
    const summaryRows: string[] = [];
    const summaryData = [
      { label: "Invoiced", value: summaryTotals.invoiced },
      { label: "Received", value: summaryTotals.received },
      { label: "Outstanding", value: summaryTotals.outstanding },
      { label: "Expenses", value: summaryTotals.expenses },
      { label: "Net", value: summaryTotals.net },
    ].filter((c) => c.value !== undefined && c.value !== null);

    if (summaryData.length > 0) {
      summaryRows.push("");
      summaryRows.push("Summary");
      summaryRows.push("Label,Value");
      summaryRows.push(
        ...summaryData.map(
          (c) =>
            `"${String(c.label).replace(/"/g, '""')}","${String(
              currencyFormatter.format(c.value as number)
            ).replace(/"/g, '""')}"`
        )
      );
    }

    const csv = [...sections, ...summaryRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `reports-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [debouncedSearch, loadReportsPage, summaryTotals]);


  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    {
      key: "finance",
      label: "Invoices",
      icon: <AccountBalanceWalletOutlinedIcon />,
    },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
    {
      key: "notifications",
      label: "Notification Devices",
      icon: <NotificationsActiveOutlinedIcon />,
    },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";

  return (
    <Box
      sx={adminPageSx}
    >
      <Box
        sx={adminNavPanelSx(sidebarCollapsed)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" sx={adminNavTitleSx} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "clients") {
                      navigate("/all-data");
                    } else if (item.key === "finance") {
                      navigateTo("/all-data/invoices");
                    } else if (item.key === "expenses") {
                      navigateTo("/all-data/expenses");
                    } else if (item.key === "reports") {
                      navigateTo("/all-data/reports");
                    } else if (item.key === "notifications") {
                      navigate("/all-data/notification-devices");
                    }
                  }}
                  sx={adminNavItemSx(selected, sidebarCollapsed)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          // Page is pulled up under the header (adminPageSx); give it back here.
          pt: { xs: 3, sm: 5 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <PageHeader
          title={activeTabLabel}
          subtitle={ADMIN_DATA_SUBTITLES[activeTab]}
          icon={navItems.find((item) => item.key === activeTab)?.icon}
        />

        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "grid",
              gap: { xs: 2, sm: 3 },
              gridTemplateColumns: {
                xs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                lg: "repeat(5, minmax(0, 1fr))",
              },
            }}
          >
            {[
              {
                label: "Invoiced",
                value: summaryTotals.invoiced,
                icon: <ReceiptLongIcon />,
                color: COLORS.MainColor,
                valueColor: COLORS.MainColor,
              },
              {
                label: "Received",
                value: summaryTotals.received,
                icon: <PaymentsIcon />,
                color: COLORS.MainColor,
                valueColor: COLORS.MainColor,
              },
              {
                label: "Outstanding",
                value: summaryTotals.outstanding,
                icon: <PendingActionsIcon />,
                color: COLORS.MainColor,
                valueColor: COLORS.MainColor,
              },
              {
                label: "Expenses",
                value: summaryTotals.expenses,
                icon: <MoneyOffIcon />,
                color: COLORS.MainColor,
                valueColor: COLORS.MainColor,
              },
              {
                label: "Net Cash",
                value: summaryTotals.net,
                icon: <AccountBalanceWalletIcon />,
                color: COLORS.MainColor,
                valueColor: COLORS.MainColor,
              },
            ].map((card) => (
              <Card
                key={card.label}
                sx={{
                  borderRadius: 4,
                  boxShadow: 2,
                  minHeight: 150,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  position: "relative",
                  bgcolor: COLORS.WhiteBackground,
                  border: "1px solid " + COLORS.BorderColor,
                }}
              >
                <CardContent sx={{ position: "relative", zIndex: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", color: card.color, fontSize: 32 }}>
                      {card.icon}
                    </Box>
                    <CustomLabel
                      text={card.label}
                      fontWeight={700}
                      sx={{ color: COLORS.BlackText, fontSize: "0.95rem" }}
                    />
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <CustomLabel
                      text={formatMoney(card.value)}
                      fontWeight={800}
                      sx={{
                        color: card.valueColor || card.color,
                        fontSize: { xs: "1.25rem", sm: "1.3rem" },
                      }}
                    />
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                ...businesses.map((business) => ({
                  label: business.name,
                  value: business.id,
                })),
              ],
              value: businessFilter,
              onChange: (v: any) => {
                const next =
                  v === null || v === undefined || v === "" ? "all" : String(v);
                setBusinessFilter(next);
              },
              placeholder: "Business",
              label: "Business",
              size: "small",
              searchable: false,
              clearable: true,
              sx: { width: 200 },
            }}
            dateRangeConfig={{
              value: reportDateRange ?? undefined,
              onChange: (v: any) => setReportDateRange(v),
              fullWidth: false,
            }}
            buttonConfig={[
              {
                label: "Export CSV",
                onClick: (reportProgress) => exportReportsCsv(reportProgress),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                disabled: !financeUnlocked,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminReportRow>
              filters={{
                page: 0,
                size: ADMIN_REPORTS_PAGE_SIZE,
                search: debouncedSearch,
                businessFilter,
                dateRange: JSON.stringify(reportDateRange ?? null),
                financeUnlocked,
              }}
              shadeRow={(row) => {
                const status = getPaymentStatus(row);
                return status === "unpaid" || status === "partial";
              }}
              shadeBg={(row) => {
                const status = getPaymentStatus(row);
                return status === "unpaid" ? "#FFF1E6" : "#FFF6E6";
              }}
              shadeColor="inherit"
              fields={[
                { key: "title", header: "Title" },
                {
                  key: "totalAmount",
                  header: "Total Amount",
                  transform: (r) =>
                    r.totalAmount === undefined || r.totalAmount === null
                      ? "-"
                      : (() => {
                          const val = parseAmountValue(r.totalAmount);
                          const formatted = currencyFormatter.format(val);
                          const isExpense = Number(r.amount || 0) < 0;
                          return (
                            <span
                              style={{
                                color: isExpense
                                  ? COLORS.placeHolderColor
                                  : undefined,
                              }}
                            >
                              {formatted}
                            </span>
                          );
                        })(),
                },
                {
                  key: "paidAmount",
                  header: "Paid",
                  transform: (r) =>
                    r.paidAmount === undefined || r.paidAmount === null
                      ? "-"
                      : (() => {
                          const val = parseAmountValue(r.paidAmount);
                          const formatted = currencyFormatter.format(val);
                          const isExpense = Number(r.amount || 0) < 0;
                          return (
                            <span
                              style={{
                                color: isExpense
                                  ? COLORS.placeHolderColor
                                  : undefined,
                              }}
                            >
                              {formatted}
                            </span>
                          );
                        })(),
                },
                {
                  key: "remainingAmount",
                  header: "Remaining",
                  transform: (r) =>
                    r.remainingAmount === undefined || r.remainingAmount === null
                      ? "-"
                      : (() => {
                          const val = parseAmountValue(r.remainingAmount);
                          const formatted = currencyFormatter.format(val);
                          const status = getPaymentStatus(r);
                          const isExpense = Number(r.amount || 0) < 0;
                          const color =
                            status === "unpaid" || status === "partial"
                              ? COLORS.ORANGE
                              : isExpense || val === 0
                                ? COLORS.placeHolderColor
                                : undefined;
                          return (
                            <span
                              style={{
                                color,
                                fontWeight:
                                  status === "unpaid" || status === "partial"
                                    ? 700
                                    : undefined,
                              }}
                            >
                              {formatted}
                            </span>
                          );
                        })(),
                },
                {
                  key: "amount",
                  header: "Net Amount",
                  transform: (r) => {
                    const val = Number(r.amount || 0);
                    const formatted = currencyFormatter.format(Math.abs(val));
                    return val > 0 ? (
                      <span style={{ color: COLORS.SUCCESS }}>
                        +{formatted}
                      </span>
                    ) : (
                      <span style={{ color: COLORS.RED }}>-{formatted}</span>
                    );
                  },
                },
                {
                  key: "date",
                  header: "Date",
                  transform: (r) => formatTableDate(r.date),
                },
                { key: "business_name", header: "Business" },
              ]}
              fetchFunc={fetchReportsTable}
              loading={loadingReports}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: COLORS.WhiteBackground,
              borderRadius: "0 0 10px 10px",
              padding: "16px",
              marginTop: "-10px",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <CustomLabel
              text="Total Net Amount:"
              sx={{ color: COLORS.typographyColor }}
            />
            <CustomLabel
              text={currencyFormatter.format(netTotal)}
              fontWeight="bold"
              sx={{ color: COLORS.MainColor }}
            />
          </Box>
        </Box>
      </Box>
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={() => setPinDialogOpen(false)}
        onVerified={handlePinVerified}
        title="Finance access"
        description="Enter the PIN to view admin reports."
      />
    </Box>
  );
};

export default AdminReports;
